import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BuscaPaciente, type PacienteResumo } from '../componentes/BuscaPaciente.tsx';
import { CamposDoExame, exameVazio, type FormExame } from '../componentes/CamposDoExame.tsx';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Aviso, Botao, Campo, Chapa, Selecao, type Estagio } from '../componentes/forma.tsx';
import { api, ErroApi, query } from '../lib/api.ts';
import { useOpcoes } from '../lib/cadastro.ts';
import { dataBr, hojeIso, idade } from '../lib/formato.ts';
import { usePode } from '../lib/sessao.tsx';
import type { ExameLinha } from './Fila.tsx';
import './atendimento.css';

/* O atendimento do balcão, de ponta a ponta, sem trocar de tela.
 *
 * Começa pela BUSCA do paciente e não pelo cadastro dele -- são dezessete mil
 * pacientes migrados, e quem chega ao balcão quase sempre já existe. Começar
 * pelo cadastro faria o caso comum passar por uma tela que não precisa e, pior,
 * convidaria à duplicata: paciente cadastrado duas vezes racha o histórico em
 * dois, e ninguém percebe até procurarem um exame antigo e não acharem.
 *
 * Quem não existe é cadastrado aqui mesmo, com o nome já preenchido do que foi
 * digitado na busca, e o exame abre em seguida. */

interface Novo {
  nome: string;
  nascimento: string;
  sexo: string;
  cpf: string;
  telefone: string;
}

export function Atendimento() {
  const pode = usePode();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const opcoes = useOpcoes();

  const [paciente, setPaciente] = useState<PacienteResumo | null>(null);
  const [novo, setNovo] = useState<Novo | null>(null);
  const [form, setForm] = useState<FormExame>(() => exameVazio(hojeIso()));
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState('');
  const [feitos, setFeitos] = useState<
    { id: string; codigo: number; nome: string; situacao: Estagio }[]
  >([]);

  /* Alguém com o mesmo nome e a mesma data de nascimento já cadastrado.
     É aqui que a duplicata é barrada: não bloqueia -- duas pessoas podem
     mesmo coincidir --, mas obriga a olhar antes de criar a segunda. */
  const parecidos = useQuery({
    queryKey: ['pacientes', 'parecidos', novo?.nome, novo?.nascimento],
    queryFn: ({ signal }) =>
      api.get<{ itens: PacienteResumo[] }>(
        `/pacientes${query({ busca: novo!.nome, porPagina: 10 })}`,
        signal,
      ),
    enabled: !!novo && novo.nome.trim().length >= 3 && !!novo.nascimento,
    staleTime: 10_000,
  });
  const mesmos = (parecidos.data?.itens ?? []).filter(
    (p) => p.nascimento === novo?.nascimento,
  );

  /* Depois de gravar, o foco vai para o Exame -- o unico campo que sobrou para
     preencher. Num efeito, e nao dentro do `onSuccess`: la o React ainda nao
     repintou o campo, e o foco se perdia na repintura. `feitos.length` e o
     gatilho porque ele muda uma vez por exame gravado. */
  useEffect(() => {
    if (feitos.length === 0) return;
    document.querySelector<HTMLSelectElement>('select[name="procedimentoId"]')?.focus();
  }, [feitos.length]);

  const criarPaciente = useMutation({
    mutationFn: (d: Novo) => api.post<PacienteResumo>('/pacientes', d),
    onSuccess: (p) => {
      void qc.invalidateQueries({ queryKey: ['pacientes'] });
      setPaciente(p);
      setNovo(null);
      setCampos({});
    },
    onError: (e) => {
      if (e instanceof ErroApi) {
        setCampos(e.campos);
        if (!e.temCampos) setErroGeral(e.message);
      } else setErroGeral('Não foi possível cadastrar o paciente.');
    },
  });

  const criarExame = useMutation({
    mutationFn: (d: FormExame) => api.post<ExameLinha>('/exames', d),
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ['exames'] });
      setFeitos((f) => [
        ...f,
        { id: e.id, codigo: e.codigo, nome: e.procedimentoNome, situacao: e.situacao },
      ]);

      /* Só o Exame é limpo. Empresa, convênio, tipo, médico, função, data --
         tudo o que foi digitado continua ali.

         Era `exameVazio()`, que zerava o formulário inteiro. A intenção estava
         escrita neste comentário desde o começo e o código fazia o contrário:
         quem lançava três incidências do mesmo paciente redigitava três vezes
         a mesma empresa e o mesmo convênio.

         Os valores tambem ficam. O risco de cobrar o exame novo pelo preço do
         anterior foi resolvido do outro lado, em `aoTrocarProcedimento`: a
         tabela do procedimento escolhido passou a mandar no campo. Então o
         valor que sobra aqui só sobrevive até a escolha do exame -- e sobrevive
         de vez quando o procedimento não tem preço cadastrado, que é
         exatamente o caso em que digitar de novo seria trabalho perdido. */
      setForm((f) => ({ ...f, procedimentoId: '' }));
      setCampos({});
      setErroGeral('');
    },
    onError: (e) => {
      if (e instanceof ErroApi) {
        setCampos(e.campos);
        if (!e.temCampos) setErroGeral(e.message);
      } else setErroGeral('Não foi possível cadastrar o exame.');
    },
  });

  function gravarPaciente(ev: FormEvent) {
    ev.preventDefault();
    setCampos({});
    setErroGeral('');
    criarPaciente.mutate(novo!);
  }

  function gravarExame(ev: FormEvent) {
    ev.preventDefault();
    setCampos({});
    setErroGeral('');
    criarExame.mutate({ ...form, pacienteId: paciente?.id ?? '' });
  }

  function recomecar() {
    setPaciente(null);
    setNovo(null);
    setForm(exameVazio(hojeIso()));
    setCampos({});
    setErroGeral('');
    setFeitos([]);
  }

  if (!pode('pode_exame_cadastrar')) {
    return (
      <Layout>
        <Cabecalho titulo="Atendimento" />
        <Aviso>Você não tem permissão para cadastrar exames.</Aviso>
      </Layout>
    );
  }

  const passo = paciente ? 2 : 1;

  return (
    <Layout>
      <Cabecalho titulo="Novo atendimento">
        {feitos.length > 0 && (
          <Botao variante="neutro" onClick={() => navegar('/fila')}>
            Ir para a fila
          </Botao>
        )}
      </Cabecalho>

      <ol className="atend__passos" aria-label="Etapas">
        <li data-estado={passo === 1 ? 'agora' : 'feito'}>
          <span className="atend__num">1</span> Paciente
        </li>
        <li data-estado={passo === 2 ? 'agora' : 'espera'}>
          <span className="atend__num">2</span> Exame
        </li>
      </ol>

      {erroGeral && <Aviso>{erroGeral}</Aviso>}

      {/* ------------------------------------------------------- passo 1 */}

      {passo === 1 && !novo && (
        <section className="cartao atend__cartao">
          <h2 className="atend__titulo">Quem é o paciente?</h2>
          <p className="campo__dica atend__ajuda">
            Procure por nome, CPF, matrícula ou data de nascimento. Se não existir, dá para
            cadastrar aqui mesmo.
          </p>
          <div className="grade">
            <BuscaPaciente
              valor={null}
              aoEscolher={setPaciente}
              aoCadastrar={(digitado) =>
                setNovo({
                  // Só aproveita o que foi digitado se for nome: quem procurou
                  // por CPF ou data não quer isso no campo do nome.
                  nome: /\d/.test(digitado) ? '' : digitado.toUpperCase(),
                  nascimento: '',
                  sexo: '',
                  cpf: '',
                  telefone: '',
                })
              }
            />
          </div>
        </section>
      )}

      {passo === 1 && novo && (
        <form className="cartao atend__cartao" onSubmit={gravarPaciente} noValidate>
          <h2 className="atend__titulo">Cadastrar paciente</h2>
          <p className="campo__dica atend__ajuda">
            Nome e nascimento bastam para começar. O resto pode ser completado depois, na tela
            de Pacientes.
          </p>

          <div className="grade">
            <Campo
              rotulo="Nome completo"
              obrigatorio
              autoFocus
              value={novo.nome}
              erro={campos.nome}
              onChange={(e) => setNovo({ ...novo, nome: e.target.value.toUpperCase() })}
            />
            <Campo
              rotulo="Data de nascimento"
              type="date"
              largura="meia"
              obrigatorio
              value={novo.nascimento}
              erro={campos.nascimento}
              onChange={(e) => setNovo({ ...novo, nascimento: e.target.value })}
            />
            <Selecao
              rotulo="Sexo"
              largura="meia"
              value={novo.sexo}
              erro={campos.sexo}
              onChange={(e) => setNovo({ ...novo, sexo: e.target.value })}
            >
              <option value="">Não informado</option>
              <option value="F">Feminino</option>
              <option value="M">Masculino</option>
              <option value="O">Outro</option>
            </Selecao>
            <Campo
              rotulo="CPF"
              largura="meia"
              inputMode="numeric"
              value={novo.cpf}
              erro={campos.cpf}
              onChange={(e) => setNovo({ ...novo, cpf: e.target.value })}
              dica="Exame de criança costuma não ter."
            />
            <Campo
              rotulo="Telefone"
              largura="meia"
              inputMode="numeric"
              value={novo.telefone}
              erro={campos.telefone}
              onChange={(e) => setNovo({ ...novo, telefone: e.target.value })}
              dica="Usado para avisar quando o laudo ficar pronto."
            />
          </div>

          {mesmos.length > 0 && (
            <div className="atend__parecidos">
              <p className="atend__parecidos-titulo">
                {mesmos.length === 1
                  ? 'Já existe alguém com este nome e esta data de nascimento'
                  : 'Já existem cadastros com este nome e esta data de nascimento'}
              </p>
              <ul>
                {mesmos.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="atend__parecido"
                      onClick={() => {
                        setPaciente(p);
                        setNovo(null);
                      }}
                    >
                      <strong>{p.nome}</strong>
                      <span className="mono">
                        {dataBr(p.nascimento)} · {idade(p.nascimento)} anos
                      </span>
                      <span className="atend__usar">Usar este</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="campo__dica">
                Se for a mesma pessoa, use o cadastro que já existe — dois cadastros dividem o
                histórico em dois.
              </p>
            </div>
          )}

          <div className="atend__acoes">
            <Botao
              type="submit"
              variante="primario"
              carregando={criarPaciente.isPending}
              disabled={!novo.nome.trim() || !novo.nascimento}
            >
              {mesmos.length > 0 ? 'Cadastrar mesmo assim' : 'Cadastrar e continuar'}
            </Botao>
            <Botao variante="neutro" type="button" onClick={() => setNovo(null)}>
              Voltar à busca
            </Botao>
          </div>
        </form>
      )}

      {/* ------------------------------------------------------- passo 2 */}

      {passo === 2 && paciente && (
        <>
          <div className="cartao atend__paciente">
            <div>
              <strong>{paciente.nome}</strong>
              <span className="atend__paciente-apoio mono">
                {dataBr(paciente.nascimento)} · {idade(paciente.nascimento)} anos
              </span>
            </div>
            <button type="button" className="atend__trocar" onClick={recomecar}>
              Trocar paciente
            </button>
          </div>

          {feitos.length > 0 && (
            <div className="cartao atend__feitos">
              <p className="atend__feitos-titulo">
                {feitos.length === 1 ? 'Exame criado' : `${feitos.length} exames criados`}
              </p>
              <ul>
                {/* A `Chapa` e como o codigo do exame aparece em todo o resto
                    do sistema -- fila, editor, laudo, quarentena. Aqui ele
                    saia como numero solto em negrito, e era a unica tela fora
                    do padrao: quem acabou de criar o exame ve um formato, e ao
                    procurar o mesmo exame na fila ve outro. */}
                {feitos.map((f) => (
                  <li key={f.id}>
                    <Link to={`/exames/${f.id}`}>
                      <Chapa codigo={f.codigo} estagio={f.situacao} /> {f.nome}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="campo__dica">
                Pode lançar outro exame para {paciente.nome.split(' ')[0]} logo abaixo — é o
                caso de quem faz mais de uma incidência na mesma visita.
              </p>
            </div>
          )}

          <form className="cartao atend__cartao" onSubmit={gravarExame} noValidate>
            <h2 className="atend__titulo">
              {feitos.length > 0 ? 'Outro exame para este paciente' : 'Dados do exame'}
            </h2>
            <div className="grade">
              <CamposDoExame
                form={form}
                campos={campos}
                opcoes={opcoes.data}
                paciente={paciente}
                aoMudar={(m) => setForm((f) => ({ ...f, ...m }))}
                aoEscolherPaciente={() => {}}
                mostrarPaciente={false}
                mostrarValores={pode('pode_financeiro')}
              />
            </div>
            <div className="atend__acoes">
              <Botao type="submit" variante="primario" carregando={criarExame.isPending}>
                Cadastrar exame
              </Botao>
              {feitos.length > 0 && (
                <Botao variante="neutro" type="button" onClick={recomecar}>
                  Atender outra pessoa
                </Botao>
              )}
            </div>
          </form>
        </>
      )}
    </Layout>
  );
}
